import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";
import recommandedIcon from "../assets/icons/reccomnadedIocn.png";
import popularIcon from "../assets/icons/popularIocn.png";
import addIcon from "../assets/icons/addIcon1.png";
import "../css/EnterpriseDetails.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import editIcon from "../assets/Images/Edit.png";
import cpyIcon from "../assets/icons/Copy.png";
import crossIcon from "../assets/icons/crossIcon.png";
import { toast } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { formatDateToUserTimeZone } from "../utils/formatDate";

const API_BASE = process.env.REACT_APP_API_URL;

const EnterpriseSettings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const enterprise = location.state?.enterprise;

  const [showSubscription, setShowSubscription] = useState(true);
  const [showOrganization, setShowOrganization] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showModelSettings, setShowModelSettings] = useState(true);
  const [existingModelSettings, setExistingModelSettings] = useState([]);
  const [isModelSettingsFetched, setIsModelSettingsFetched] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(true);
  const [documentation, setDocumentation] = useState({
    auto_approve: false,
  });
  const [hasDocId, setHasDocId] = useState(false); // For backend check
  const [isValidating, setIsValidating] = useState(false);
  const [modelSettings, setModelSettings] = useState({
    recommended_models: [],
    popular_models: [],
  });

  const [availableModels, setAvailableModels] = useState([]);

  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("OpenAI");
  const [apiKey, setApiKey] = useState("");
  const [apiNotes, setApiNotes] = useState("");
  const [apiKeys, setApiKeys] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchModels = async () => {
    try {
      console.log("🔹 Fetching available models...");
      const res = await api.get(
        `${API_BASE}/enterprise-model-settings/enterprise-active-model-settings`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      console.log("📌 Available models API response:", res.data);

      const available = res.data.data.filter(
        (item) =>
          item.model_details && // ✅ prevent null
          item.model_details.server_status === true && // ✅ prevent inactive server
          item.model_details.model_download_status === "1" &&
          item.model_details.model_status === "Approved",
      );

      console.log("🎯 Filtered available models:", available);

      setAvailableModels(available);
    } catch (err) {
      console.error("❌ Error fetching available models:", err);
      showErrorToast(TOAST_MESSAGES.MODEL.FETCH_ERROR);
    }
  };

  const fetchApiKeys = async () => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const enterpriseFid = enterprise?.enterprise_id;

    // ✅ Ensure token and enterprise ID exist
    if (!token || !enterpriseFid) {
      console.warn("Token or enterprise ID missing, cannot fetch API keys");
      return;
    }

    try {
      const res = await api.get(`${API_BASE}/enterprise-api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { enterprise_fid: enterpriseFid }, // pass enterprise ID
      });

      // ✅ Log cleanly for debugging
      console.log("Fetched API keys:", res.data);

      // ✅ Update state safely
      setApiKeys(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch API keys:", err);
      showErrorToast("Failed to fetch API keys");
    }
  };

  // 1️⃣ Fetch available models
  useEffect(() => {
    fetchModels();
    fetchApiKeys();
  }, []);

  // 2️⃣ Fetch existing enterprise model settings
  useEffect(() => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    if (!token || !enterprise?.enterprise_id) return;

    const fetchSettings = async () => {
      try {
        console.log("🔹 Fetching existing enterprise model settings...");
        const res = await api.get(
          `${API_BASE}/enterprise-admin-setting/byEnterprise/${enterprise.enterprise_id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        console.log("📌 Existing model settings API response:", res.data);

        setExistingModelSettings(res.data || []);
        setIsModelSettingsFetched(true);
      } catch (err) {
        console.error("❌ Error fetching existing settings:", err);
      }
    };

    fetchSettings();
  }, [enterprise]);

  // 3️⃣ Combine available models and existing settings
  useEffect(() => {
    console.log("🔄 Combining models after both APIs fetched...");
    console.log("Available models:", availableModels);
    console.log("Existing model settings:", existingModelSettings);
    console.log("Is settings fetched?", isModelSettingsFetched);

    if (!availableModels.length || !isModelSettingsFetched) return;

    const recommendedModelsFromDB = existingModelSettings
      .filter((item) => item.enterprise_user_model_setting_status == "1")
      .map((item) => item.model_fid?.trim()) // use model_fid
      .filter(Boolean);

    console.log("recommanded model from db ", recommendedModelsFromDB);

    setModelSettings((prev) => ({
      ...prev,
      recommended_models: recommendedModelsFromDB,
    }));
  }, [availableModels, existingModelSettings, isModelSettingsFetched]);

  const [subscription, setSubscription] = useState({
    enterprise_subscription_name: "",
    enterprise_subscription_duration: "",
    enterprise_subscription_prize: "",
    enterprise_subscription_start_date: "",
    enterprise_subscription_end_date: "",
  });

  const [organization, setOrganization] = useState({
    enterprise_organisation_settings_theme: "",
    can_admin_view_chat: "",
    can_power_user_view_chat: "",
    data_retention_period: "",
    allowed_doc_size_mb: "",
    training_frequency: "",
    enterprise_organisation_settings_selected_model: "",
  });

  const [disclaimer, setDisclaimer] = useState({
    enterprise_disclaimer_title: "",
    enterprise_disclaimer_display_location: "",
    enterprise_disclaimer_description: "",
  });

  const [hasSubId, setHasSubId] = useState(false);
  const [hasOrgId, setHasOrgId] = useState(false);
  const [hasDisId, setHasDisId] = useState(false);

  const handleInput = (setter, key) => (e) =>
    setter((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    const id = enterprise?.enterprise_id;
    if (!id) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const headers = { Authorization: `Bearer ${token}` };

    api
      .get(`${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasSubId(true);
          setSubscription(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch subscription failed", err);
      });

    api
      .get(`${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasOrgId(true);
          setOrganization(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch organization settings failed", err);
      });

    // api
    //   .get(`${API_BASE}/enterprise-disclaimer/byEnterprise/${id}`, {
    //     headers,
    //   })
    //   .then((res) => {
    //     if (res.data) {
    //       setHasDisId(true);
    //       setDisclaimer(res.data);
    //     }
    //   })
    //   .catch((err) => {
    //     console.error("Fetch disclaimer failed", err);
    //   });

    api
      .get(`${API_BASE}/enterprise-document-settings/byEnterprise/${id}`, {
        headers,
      })
      .then((res) => {
        if (res.data) {
          setHasDocId(true);
          setDocumentation(res.data);
        }
      })
      .catch((err) => {
        console.error("Fetch documentation settings failed", err);
      });
  }, [enterprise]);

  const handleSave = async () => {
    const id = enterprise?.enterprise_id;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    const headers = { Authorization: `Bearer ${token}` };

    try {
      // === Subscription ===
      if (hasSubId) {
        await api.put(
          `${API_BASE}/enterprise-subscriptions/byEnterprise/${id}`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-subscriptions`,
          { ...subscription, enterprise_fid: id },
          { headers },
        );
      }

      // === Organization ===
      if (hasOrgId) {
        await api.put(
          `${API_BASE}/enterprise-organisation-settings/byEnterprise/${id}`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-organisation-settings`,
          { ...organization, enterprise_fid: id },
          { headers },
        );
      }

      // === Documentation Settings ===
      if (hasDocId) {
        await api.put(
          `${API_BASE}/enterprise-document-settings/byEnterprise/${id}`,
          { ...documentation, enterprise_fid: id },
          { headers },
        );
      } else {
        await api.post(
          `${API_BASE}/enterprise-document-settings`,
          { ...documentation, enterprise_fid: id },
          { headers },
        );
      }

      // === Disclaimer ===
      // if (hasDisId) {
      //   await api.put(
      //     `${API_BASE}/enterprise-disclaimer/byEnterprise/${id}`,
      //     { ...disclaimer, enterprise_fid: id },
      //     { headers }
      //   );
      // } else {
      //   await api.post(
      //     `${API_BASE}/enterprise-disclaimer`,
      //     { ...disclaimer, enterprise_fid: id },
      //     { headers }
      //   );
      // }

      // === Unified success message ===
      showSuccessToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_SUCCESS);
      setTimeout(() => navigate(-1), 1200); // short delay for visibility
    } catch (err) {
      console.error("Save failed:", err);
      showErrorToast(TOAST_MESSAGES.ENTERPRISE_SETTINGS.SAVE_ERROR);
    }

    const modelSettingPayloads = [];

    // 1. Track processed model IDs to avoid duplication
    const processedModelIds = new Set();

    // 2. Create or update selected models (status: "1")
    modelSettings.recommended_models.forEach((modelName) => {
      const foundModel = availableModels.find(
        (m) => m.model_details?.model_name === modelName,
      );

      if (!foundModel) return;

      const modelId = foundModel.model_details.model_id;
      processedModelIds.add(modelId);

      const existing = existingModelSettings.find(
        (ems) => ems.model_fid === modelId,
      );

      if (existing) {
        // Update to status "1"
        modelSettingPayloads.push(
          api.put(
            `${API_BASE}/enterprise-admin-setting/byEnterprise/${id}`,
            {
              model_fid: modelId,
              enterprise_model_setting_status: "1",
            },
            { headers },
          ),
        );
      } else {
        // Create new
        modelSettingPayloads.push(
          api.post(
            `${API_BASE}/enterprise-admin-setting`,
            {
              enterprise_fid: id,
              model_fid: modelId,
              enterprise_model_setting_status: "1",
            },
            { headers },
          ),
        );
      }
    });

    // 3. Deactivate models that were previously saved but now unchecked (status: "0")
    existingModelSettings.forEach((existing) => {
      if (!processedModelIds.has(existing.model_fid)) {
        modelSettingPayloads.push(
          api.put(
            `${API_BASE}/enterprise-admin-setting/byEnterprise/${id}`,
            {
              model_fid: existing.model_fid,
              enterprise_model_setting_status: "0",
            },
            { headers },
          ),
        );
      }
    });

    // 4. Execute all API calls
    await Promise.all(modelSettingPayloads);

    await Promise.all(modelSettingPayloads);
  };

  const handleValidateApi = async () => {
    const enterprise_fid = enterprise?.enterprise_id;
    if (!apiKey.trim()) return showErrorToast("API Key is required!");
    if (!selectedProvider) return showErrorToast("Please select a provider!");

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    try {
      setIsValidating(true);

      /* ===============================
       1️⃣ STEP 1: VALIDATE API KEY
    =============================== */
      const validateRes = await api.post(
        `${process.env.REACT_APP_API_URL}/enterprise-model-settings/validate-and-assign`,
        {
          provider: selectedProvider.toLowerCase(),
          api_key: apiKey,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("✅ API Validation Success:", validateRes.data);

      /* ===============================
       2️⃣ STEP 2: SAVE OR UPDATE
    =============================== */
      const payload = {
        provider_name: selectedProvider,
        api_key: apiKey,
        enterprise_fid,
      };

      if (editingRecord) {
        // 🔁 UPDATE (only after validation success)
        await api.put(
          `${API_BASE}/enterprise-api-keys/${editingRecord.enterprise_api_key_setting_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        showSuccessToast(`${selectedProvider} API Key Updated Successfully ✔`);
      } else {
        // ➕ CREATE
        await api.post(`${API_BASE}/enterprise-api-keys`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        showSuccessToast(`${selectedProvider} API Key Added Successfully ✔`);
      }

      /* ===============================
       3️⃣ FINAL SUCCESS CLEANUP
    =============================== */
      fetchApiKeys(); // refresh table
      setShowApiModal(false); // close modal
      setApiKey(""); // clear input
      setEditingRecord(null); // reset edit state
    } catch (error) {
      console.error("❌ API validation or save failed:", error);

      const msg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        "API validation or save failed!";

      // 🔒 IMPORTANT:
      // We DO NOT clear apiKey
      // We DO NOT close modal
      // Old key remains safe in DB
      showErrorToast(msg);
    } finally {
      setIsValidating(false);
    }
  };

  const maskApiKey = (key) => {
    if (!key) return "—";

    const visibleStart = 4; // show first 4 chars
    const visibleEnd = 4; // show last 4 chars

    if (key.length <= visibleStart + visibleEnd) return key; // short keys, show fully

    const maskedMiddle = "*".repeat(key.length - visibleStart - visibleEnd);
    return key.slice(0, visibleStart) + maskedMiddle + key.slice(-visibleEnd);
  };

  const handleEdit = (row) => {
    // Store full record for update flow
    setEditingRecord(row);

    // Provider is fixed during update (disabled in modal)
    setSelectedProvider(row.provider_name);

    // 🔐 Never prefill API key for security
    setApiKey("");

    // Open modal
    setShowApiModal(true);
  };

  const handleChangeStatus = async (row) => {
    try {
      const newStatus = row.enterprise_api_key_status === "1" ? "0" : "1";

      await api.patch(
        `${API_BASE}/enterprise-api-keys/${row.enterprise_api_key_setting_id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showSuccessToast(
        `API Key ${
          newStatus === "1" ? "Activated" : "Deactivated"
        } Successfully`,
      );

      fetchApiKeys(); // refresh table
    } catch (error) {
      console.error("Failed to change API key status:", error);

      showErrorToast(
        error.response?.data?.message || "Failed to update status",
      );
    }
  };

  // 🔹 Delete API Key (hard delete)
  const handleDeleteApiKey = async (record) => {
    if (!record) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete the API key for ${record.provider_name}?`,
    );
    if (!confirmDelete) return;

    try {
      const provider = record.provider_name.toLowerCase();

      // 1️⃣ Find all ONLINE models related to this provider
      const providerModelNames = availableModels
        .filter(
          (m) =>
            m.model_details?.model_type === "Online" &&
            (m.model_details?.model_provider || "").toLowerCase() === provider,
        )
        .map((m) => m.model_fid.toLowerCase());

      // 2️⃣ Update state (uncheck models)
      setModelSettings((prev) => {
        const updatedModels = prev.recommended_models.filter(
          (m) => !providerModelNames.includes(m.toLowerCase()),
        );

        return {
          ...prev,
          recommended_models: updatedModels,
        };
      });

      // 3️⃣ Delete API key
      await api.delete(
        `${API_BASE}/enterprise-api-keys/${record.enterprise_api_key_setting_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      showSuccessToast("API Key deleted successfully ✔");
      fetchApiKeys();

      // 4️⃣ AUTO-SAVE AFTER STATE UPDATE
      // setTimeout(() => {
      //   handleSave(); // ✅ uses updated recommended_models
      // }, 0);
    } catch (error) {
      console.error("Failed to delete API key:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to delete API key",
      );
    }
  };


// 🔹 Delete API Key (hard delete only)
// const handleDeleteApiKey = async (record) => {
//   if (!record) return;

//   const confirmDelete = window.confirm(
//     `Are you sure you want to permanently delete the API key for ${record.provider_name}?`
//   );
//   if (!confirmDelete) return;

//   try {
//     await api.delete(
//       `${API_BASE}/enterprise-api-keys/${record.enterprise_api_key_setting_id}`,
//       {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       }
//     );

//     showSuccessToast("API Key deleted successfully ✔");

//     // Refresh API key list
//     fetchApiKeys();
//   } catch (error) {
//     console.error("Failed to delete API key:", error);
//     showErrorToast(
//       error.response?.data?.message || "Failed to delete API key"
//     );
//   }
// };



  // 🔹 Copy API Key
  const handleCopyApiKey = (key) => {
    if (!key) return;
    navigator.clipboard
      .writeText(key)
      .then(() => {
        showSuccessToast("API Key copied to clipboard ✔");
      })
      .catch((err) => {
        console.error("Failed to copy API key:", err);
        showErrorToast("Failed to copy API key");
      });
  };

  // 🔹 Mask API Key (for table display)
  // const maskApiKey = (key) => {
  //   if (!key) return "—";
  //   const visibleStart = 4;
  //   const visibleEnd = 4;
  //   if (key.length <= visibleStart + visibleEnd) return key;
  //   const maskedMiddle = "*".repeat(key.length - visibleStart - visibleEnd);
  //   return key.slice(0, visibleStart) + maskedMiddle + key.slice(-visibleEnd);
  // };

  const providersWithModels = React.useMemo(() => {
    return [
      ...new Set(
        availableModels
          .filter(
            (m) => m.model_details?.model_type?.toLowerCase() === "online",
          )
          .map((m) => m.model_details?.model_provider?.toLowerCase())
          .filter(Boolean),
      ),
    ];
  }, [availableModels]);

  console.log("providersWithModels:", providersWithModels);


  return (
    <div className="py-2 px-3 h-100 overflow-y-auto">
      <div className="">
        {/* === Subscription === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowSubscription(!showSubscription)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">1</div>
            <h5 className="section-title mb-0">Subscription Details</h5>
          </div>
          <i
            className={`bi ${
              showSubscription ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showSubscription && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Subscription Name</label>
                <select
                  className="form-select"
                  value={subscription.enterprise_subscription_name}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_name",
                  )}
                >
                  <option value="">Select Subscription</option>
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Subscription Duration</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_duration}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_duration",
                  )}
                />
              </div>
              <div className="col-md-4">
                <label>Subscription Price</label>
                <input
                  type="text"
                  className="form-control"
                  value={subscription.enterprise_subscription_prize}
                  onChange={handleInput(
                    setSubscription,
                    "enterprise_subscription_prize",
                  )}
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4">
                <label>Subscription Start Date</label>
                <DatePicker
                  className="datecolor"
                  calendarClassName="datecolor" // calendar popup
                  selected={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : null
                  }
                  onChange={(date) => {
                    const iso = date?.toISOString().split("T")[0];
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_start_date: iso,
                      enterprise_subscription_end_date:
                        prev.enterprise_subscription_end_date < iso
                          ? ""
                          : prev.enterprise_subscription_end_date,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={new Date()}
                  customInput={<CustomDateInput />}
                />
              </div>

              <div className="col-md-4">
                <label>Subscription End Date</label>
                <DatePicker
                  selected={
                    subscription.enterprise_subscription_end_date
                      ? new Date(subscription.enterprise_subscription_end_date)
                      : null
                  }
                  onChange={(date) => {
                    const iso = date?.toISOString().split("T")[0];
                    setSubscription((prev) => ({
                      ...prev,
                      enterprise_subscription_end_date: iso,
                    }));
                  }}
                  dateFormat="MM/dd/yyyy"
                  minDate={
                    subscription.enterprise_subscription_start_date
                      ? new Date(
                          subscription.enterprise_subscription_start_date,
                        )
                      : new Date()
                  }
                  customInput={<CustomDateInput />}
                />
              </div>
            </div>
          </>
        )}

        {/* === Organization === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowOrganization(!showOrganization)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">2</div>
            <h5 className="section-title mb-0">Organization Settings</h5>
          </div>
          <i
            className={`bi ${
              showOrganization ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showOrganization && (
          <>
            <div className="row mb-3">
              <div className="col-md-4">
                <label>Theme</label>
                <select
                  className="form-select"
                  value={organization.enterprise_organisation_settings_theme}
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_theme",
                  )}
                >
                  <option value="">Select Theme</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Select Model</label>
                <select
                  className="form-select"
                  value={
                    organization.enterprise_organisation_settings_selected_model
                  }
                  onChange={handleInput(
                    setOrganization,
                    "enterprise_organisation_settings_selected_model",
                  )}
                >
                  <option value="">Choose Model</option>
                  <option value="gpt-3.5">GPT-3.5</option>
                  <option value="gpt-4">GPT-4</option>
                  <option value="custom">Custom Model</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Training Frequency</label>
                <select
                  className="form-select"
                  value={organization.training_frequency}
                  onChange={handleInput(setOrganization, "training_frequency")}
                >
                  <option value="">Select Frequency</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              {/* <div className="col-md-4">
                <label>Can Super Admin View Chat</label>
                <select
                  className="form-select"
                  value={organization.can_admin_view_chat}
                  onChange={handleInput(setOrganization, "can_admin_view_chat")}
                >
                  <option value="">Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Can Super Admin View Chat</label>
                <select
                  className="form-select"
                  value={organization.can_power_user_view_chat}
                  onChange={handleInput(
                    setOrganization,
                    "can_power_user_view_chat"
                  )}
                >
                  <option value="">Select Option</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </div> */}
            </div>

            <div className="row mb-3">
              <div className="col-md-4">
                <label>No Need To Store Data</label>
                <select
                  className="form-select"
                  value={organization.data_retention_period}
                  onChange={handleInput(
                    setOrganization,
                    "data_retention_period",
                  )}
                >
                  <option value="">Select Duration</option>
                  <option value="30">30 Days</option>
                  <option value="90">90 Days</option>
                </select>
              </div>
              <div className="col-md-4">
                <label>Total Allowed Document Size</label>
                <select
                  className="form-select"
                  value={organization.allowed_doc_size_mb}
                  onChange={handleInput(setOrganization, "allowed_doc_size_mb")}
                >
                  <option value="">Select Size</option>
                  <option value="100">100 MB</option>
                  <option value="500">500 MB</option>
                  <option value="1024">1 GB</option>
                </select>
              </div>
              {/* <div className="col-md-4">
                <label>Training Frequency</label>
                <select
                  className="form-select"
                  value={organization.training_frequency}
                  onChange={handleInput(setOrganization, "training_frequency")}
                >
                  <option value="">Select Frequency</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div> */}
            </div>

            <div className="row mb-4"></div>
          </>
        )}

        {/* === Disclaimer === */}
        {/* <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDisclaimer(!showDisclaimer)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">3</div>
            <h5 className="section-title mb-0">Disclaimer Settings</h5>
          </div>
          <i
            className={`bi ${
              showDisclaimer ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showDisclaimer && (
          <>
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Disclaimer Title</label>
                <input
                  className="form-control"
                  value={disclaimer.enterprise_disclaimer_title}
                  onChange={handleInput(
                    setDisclaimer,
                    "enterprise_disclaimer_title"
                  )}
                />
              </div>
              <div className="col-md-6">
                <label>Display Location</label>
                <select className="form-select" value="popup" disabled>
                  <option value="">Select Location</option>
                  <option value="top">Top</option>
                  <option value="bottom">Bottom</option>
                  <option value="popup">Popup</option>
                </select>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-12">
                <label>Disclaimer Description</label>
                <textarea
                  className="input-textarea1 p-2"
                  rows="4"
                  value={disclaimer.enterprise_disclaimer_description}
                  onChange={handleInput(
                    setDisclaimer,
                    "enterprise_disclaimer_description"
                  )}
                />
              </div>
            </div>
          </>
        )} */}

        {/* === Model Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-3"
          style={{ cursor: "pointer" }}
          onClick={() => setShowModelSettings(!showModelSettings)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">3</div>
            <h5 className="section-title mb-0">Model Settings</h5>
          </div>
          <i
            className={`bi ${
              showModelSettings ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>
        {showModelSettings && (
          //VKD83 -- List also we are getting from backend and show in UI with checkbox to select/deselect and save the recommended models for enterprise
          <div className="mb-3">
            <h6 className="d-flex align-items-center">
              <img
                src={recommandedIcon}
                alt="Recommended Icon"
                style={{ width: "25px", height: "28px" }}
                className="me-2"
              />
              Recommended Models
            </h6>

           {/* ================= OFFLINE MODELS ================= */}
<div className="fw-bold mt-2 mb-2 refresh-prompts">
  OFFLINE MODELS
</div>

<div className="row mx-2 mb-3">
  {availableModels.filter(
    (m) => m.model_details?.model_type === "Offline"
  ).length === 0 ? (
    /*VKD124 🔴 Empty State Message */
    <div className="col-12 text-muted fst-italic">
      No offline models found. Please add or download an offline model to
      continue.
    </div>
  ) : (
    availableModels
      .filter((m) => m.model_details?.model_type === "Offline")
      .map((model) => {
        const modelName = model.model_fid;

        const isChecked = modelSettings.recommended_models
          ?.map((m) => m.toLowerCase())
          .includes(modelName.toLowerCase());

        return (
          <div className="col-md-3 mb-2" key={modelName}>
            <div className="form-check">
              <input
                type="checkbox"
                className="custom-checkbox"
                checked={isChecked}
                onChange={(e) => {
                  const updated = e.target.checked
                    ? [...modelSettings.recommended_models, modelName]
                    : modelSettings.recommended_models.filter(
                        (m) =>
                          m.toLowerCase() !== modelName.toLowerCase()
                      );

                  setModelSettings((prev) => ({
                    ...prev,
                    recommended_models: updated,
                  }));
                }}
              />
              <label className="ms-2 form-check-label">
                {model.model_details?.model_generic_name || modelName}
              </label>
            </div>
          </div>
        );
      })
  )}
</div>


            {/* ================= API KEYS + ONLINE MODELS ================= */}
            <button
              className="custom-btn mt-2 mb-2 d-flex align-items-center gap-2"
              onClick={() => setShowApiModal(true)}
            >
              <img
                src={addIcon}
                alt="plus icon"
                style={{ width: "18px", height: "18px" }}
                className="icon-image"
              />
              Add API Key
            </button>

            <div className="table-responsive">
              <table className="table  mt-2 table-sm w-100">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>API Key</th>
                    <th>Online Models</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {apiKeys.length > 0 ? (
                    apiKeys.map((k) => {
                      const provider = k.provider_name.toLowerCase();

                      const providerModels = availableModels.filter(
                        (m) =>
                          m.model_details?.model_type === "Online" &&
                          (
                            m.model_details?.model_provider || ""
                          ).toLowerCase() === provider,
                      );

                      const hasApiKey = Boolean(k.api_key);

                      return (
                        <tr key={k.enterprise_api_key_setting_id}>
                          {/* Provider */}
                          <td className="fw-semibold">{k.provider_name}</td>

                          <td
                            style={{
                              maxWidth: "150px", // adjust width as needed
                              overflow: "hidden",
                              textOverflow: "clip",
                              // textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {maskApiKey(k.api_key)}
                          </td>

                          {/* Online Models */}
                          <td>
                            <div className="d-flex flex-wrap">
                              {providerModels.length > 0 ? (
                                providerModels.map((model) => {
                                  const modelName = model.model_fid;

                                  const isChecked =
                                    modelSettings.recommended_models
                                      ?.map((m) => m.toLowerCase())
                                      .includes(modelName.toLowerCase());

                                  return (
                                    <div
                                      className="form-check me-3 align-items-center d-flex"
                                      key={modelName}
                                    >
                                      <input
                                        type="checkbox"
                                        className="custom-checkbox"
                                        disabled={!hasApiKey}
                                        checked={hasApiKey && isChecked}
                                        onChange={(e) => {
                                          if (!hasApiKey) return;

                                          const updated = e.target.checked
                                            ? [
                                                ...modelSettings.recommended_models,
                                                modelName,
                                              ]
                                            : modelSettings.recommended_models.filter(
                                                (m) =>
                                                  m.toLowerCase() !==
                                                  modelName.toLowerCase(),
                                              );

                                          setModelSettings((prev) => ({
                                            ...prev,
                                            recommended_models: updated,
                                          }));
                                        }}
                                      />
                                      <label
                                        className={`ms-1 form-check-label ${
                                          !hasApiKey ? "text-muted" : ""
                                        }`}
                                      >
                                        {model.model_details
                                          ?.model_generic_name || modelName}
                                      </label>
                                    </div>
                                  );
                                })
                              ) : (
                                <span className="text-muted">No models</span>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="gap-2">
                            <button
                              className="icon-button"
                              onClick={() => handleCopyApiKey(k.api_key)}
                              title="Copy API Key"
                            >
                              <img
                                src={cpyIcon}
                                className="icon-image"
                                alt="Copy"
                              />
                            </button>

                            <button
                              className="icon-button"
                              onClick={() => handleEdit(k)}
                              title="Edit"
                            >
                              <img
                                src={editIcon}
                                className="icon-image"
                                alt="Edit"
                              />
                            </button>

                            <button
                              className="icon-button"
                              onClick={() => handleDeleteApiKey(k)}
                              title="Delete"
                            >
                              <img
                                src={crossIcon}
                                className="icon-image"
                                alt="Delete"
                              />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center text-muted">
                        No API keys found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* === Documentation Settings === */}
        <div
          className="section-header d-flex align-items-center gap-2 mb-2"
          style={{ cursor: "pointer" }}
          onClick={() => setShowDocumentation(!showDocumentation)}
        >
          <div className="d-flex align-items-center">
            <div className="section-badge me-2">4</div>
            <h5 className="section-title mb-0">Documentation Settings</h5>
          </div>
          <i
            className={`bi ${
              showDocumentation ? "bi-caret-up-fill" : "bi-caret-down-fill"
            }`}
          ></i>
        </div>

        {showDocumentation && (
          <div className="row mb-4">
            <div className="col-md-4 d-flex align-items-center">
              <label className="me-3 mb-0">Auto Approve</label>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={documentation.auto_approve || false}
                  onChange={(e) =>
                    setDocumentation((prev) => ({
                      ...prev,
                      auto_approve: e.target.checked,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        )}

        <hr className="my-4" />

        <div className="d-flex justify-content-center gap-2">
          <button
            type="button"
            className="cancle-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>
          <button type="button" className="custom-btn" onClick={handleSave}>
            Save
          </button>
        </div>
        {/* <hr className="mt-5" /> */}
      </div>

      {showApiModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-history modal-dialog-centered history-model">
            <div className="modal-content position-relative">
              {/* ❌ Close Button */}
              <button
                type="button"
                className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                onClick={() => {
                  setShowApiModal(false);
                  setEditingRecord(null);
                  setApiKey("");
                  setSelectedProvider("OpenAI");
                }}
              >
                <img
                  src={crossIcon}
                  className="icon-image cross-i"
                  alt="Close"
                />
              </button>

              {/* -------- Modal Body -------- */}
              <div className="p-4">
                <p className="fw-bold text-center mt-2 mb-4 refresh-prompts">
                  {editingRecord ? "Update API Key" : "Add API Key"}
                </p>

                {/* 🔽 Provider Select */}
                <label className="fw-semibold mb-1">Select Provider</label>
                <select
                  className="form-select mb-3"
                  value={selectedProvider}
                  onChange={(e) => setSelectedProvider(e.target.value)}
                  disabled={!!editingRecord}
                >
                  {providersWithModels.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>

                {/* 🔑 API Key Input */}
                <label className="fw-semibold mb-1">
                  {editingRecord ? "New API Key" : "API Key"}
                </label>

                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={
                    editingRecord
                      ? "Paste new API key to replace existing one..."
                      : "Paste your API key here..."
                  }
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />

                {editingRecord && (
                  <small className="text-muted d-block mt-1">
                    Existing API key will remain unchanged if validation or
                    update fails.
                  </small>
                )}
              </div>

              {/* -------- Footer Buttons -------- */}
              <div className="modal-footer justify-content-center gap-2 mb-3">
                <button
                  type="button"
                  className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                  onClick={() => {
                    setShowApiModal(false);
                    setEditingRecord(null);
                    setApiKey("");
                    setSelectedProvider("OpenAI");
                  }}
                  disabled={isValidating}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                  onClick={handleValidateApi}
                  disabled={isValidating}
                >
                  {isValidating
                    ? "Validating..."
                    : editingRecord
                      ? "Validate & Update"
                      : "Validate & Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseSettings;
